// src/useResponsive.js
// ─────────────────────────────────────────────────────────────────────────────
// Which of the three sizes we are at, as a hook.
//
// Layout that only needs to *reflow* (a grid going from one column to three)
// should be a CSS media query — those are in public/index.html as the `cc-*`
// utility classes, and they cost nothing at runtime. This hook is for the cases
// where the difference is structural rather than cosmetic: the shell swapping
// bottom navigation for a side rail, or the risk map putting its detail panel
// beside the map instead of under it. Those change which elements exist, so
// they have to be decided in JS.
//
// matchMedia rather than a resize listener: it fires only when a threshold is
// actually crossed, not on every pixel of a drag.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { BP } from "./constants";

const QUERIES = {
  tablet: `(min-width: ${BP.tablet}px)`,
  desktop: `(min-width: ${BP.desktop}px)`,
};

const read = () => {
  // Guard for a non-browser render (tests, or any future prerender step).
  if (typeof window === "undefined" || !window.matchMedia) {
    return { isTablet: false, isDesktop: false };
  }
  return {
    isTablet: window.matchMedia(QUERIES.tablet).matches,
    isDesktop: window.matchMedia(QUERIES.desktop).matches,
  };
};

/**
 * @returns {{ isMobile: boolean, isTablet: boolean, isDesktop: boolean, bp: "mobile"|"tablet"|"desktop" }}
 *
 * `isTablet` is true at tablet size AND above, so it reads as "at least
 * tablet". Use `bp` when you want exactly one of the three.
 */
export function useBreakpoint() {
  const [state, setState] = useState(read);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const lists = Object.values(QUERIES).map((q) => window.matchMedia(q));
    const onChange = () => setState(read());
    // addEventListener on a MediaQueryList is the modern form; addListener is
    // the fallback for older WebViews, which is a real target here.
    lists.forEach((l) => (l.addEventListener ? l.addEventListener("change", onChange) : l.addListener(onChange)));
    onChange();
    return () => {
      lists.forEach((l) =>
        l.removeEventListener ? l.removeEventListener("change", onChange) : l.removeListener(onChange)
      );
    };
  }, []);

  return {
    ...state,
    isMobile: !state.isTablet,
    bp: state.isDesktop ? "desktop" : state.isTablet ? "tablet" : "mobile",
  };
}
